import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Dialog,
  Slide,
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Box,
  Button,
  TextField,
  Autocomplete,
  Alert,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import {
  setAddWarningDialog,
  addWarning,
  setAddedWarningBody,
} from "../../../components/collabs/collabsSlice";
import Langs, { Localize } from "../../../app/lang/langs";
import { checkPermission } from "../../../app/utils/helpers";
import { config } from "../../../app/utils/config";

/* ------------------------------------------------------------------ */
/*  Shared style constants                                             */
/* ------------------------------------------------------------------ */
const TRANSITION_SMOOTH = "all 0.25s cubic-bezier(0.4, 0, 0.2, 1)";
const BORDER_RADIUS_INPUT = "12px";
const BORDER_RADIUS_BUTTON = "14px";

const fieldLabelSx = {
  fontWeight: 600,
  mb: 0.75,
  letterSpacing: "-0.01em",
  color: "text.secondary",
  fontSize: "0.82rem",
  textTransform: "uppercase",
};

const glassmorphicTextFieldSx = {
  mb: 3,
  "& .MuiOutlinedInput-root": {
    borderRadius: BORDER_RADIUS_INPUT,
    backgroundColor: "rgba(255,255,255,0.06)",
    backdropFilter: "blur(6px)",
    transition: TRANSITION_SMOOTH,
    "& fieldset": {
      borderColor: "rgba(0,0,0,0.08)",
      transition: TRANSITION_SMOOTH,
    },
    "&:hover fieldset": {
      borderColor: "rgba(0,0,0,0.18)",
    },
    "&.Mui-focused fieldset": {
      borderColor: "rgba(255,144,21,0.5)",
      boxShadow: "0 0 0 3px rgba(255,144,21,0.12)",
    },
    "&.Mui-focused": {
      backgroundColor: "rgba(255,255,255,0.10)",
    },
  },
  "& input": {
    fontSize: "16px",
    transition: TRANSITION_SMOOTH,
  },
  "& textarea": {
    fontSize: "16px",
  },
  "& input::placeholder, & textarea::placeholder": {
    opacity: 0.45,
  },
};

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

export default function MobileAddWarning() {
  const dispatch = useDispatch();

  const visible = useSelector((state) => state.collabs.addWarningDialog);
  const collabs = useSelector((state) => state.collabs.allCollabsList);
  const editorId = useSelector((state) => state.login.userId);
  const warningCatgories = useSelector((state) => state.collabs.warningCatgories);

  const [collab, setCollab] = useState(null);
  const [category, setCategory] = useState(null);
  const [status, setStatus] = useState(null);
  const [warningDate, setWarningDate] = useState("");
  const [comments, setComments] = useState("");
  const [addBody, setAddBody] = useState({});
  const [errors, setErrors] = useState({});
  const [showError, setShowError] = useState(false);

  const collabOptions = collabs
    .filter((c) => c.entryDate)
    .map((c) => ({
      value: c.id,
      label: c.label || `${c.nom} ${c.prenom}`,
    }));

  const categoryOptions = warningCatgories.map((cat) => ({
    label: Localize(cat.value),
    value: cat.value,
  }));

  const statusOptions = [
    { label: Localize("received"), value: "received" },
    { label: Localize("signed"), value: "signed" },
    { label: Localize("sent"), value: "sent" },
  ];

  const handleClose = () => {
    dispatch(setAddWarningDialog(false));
    clearInput();
  };

  const clearInput = () => {
    setCollab(null);
    setCategory(null);
    setStatus(null);
    setWarningDate("");
    setComments("");
    setAddBody({});
    setErrors({});
    setShowError(false);
  };

  const handleCollabChange = (_, newValue) => {
    setCollab(newValue);
    if (newValue) {
      setAddBody((prev) => ({ ...prev, id_collab: newValue.value }));
      setErrors((prev) => ({ ...prev, collab: "" }));
    } else {
      setAddBody((prev) => {
        const newState = { ...prev };
        delete newState.id_collab;
        return newState;
      });
    }
  };

  const handleCategoryChange = (_, newValue) => {
    setCategory(newValue);
    if (newValue) {
      setAddBody((prev) => ({ ...prev, category: newValue.value }));
    } else {
      setAddBody((prev) => {
        const newState = { ...prev };
        delete newState.category;
        return newState;
      });
    }
  };

  const handleStatusChange = (_, newValue) => {
    setStatus(newValue);
    if (newValue) {
      setAddBody((prev) => ({ ...prev, status: newValue.value }));
    } else {
      setAddBody((prev) => {
        const newState = { ...prev };
        delete newState.status;
        return newState;
      });
    }
  };

  const handleWarningDateChange = (e) => {
    const date = e.target.value;
    setWarningDate(date);
    if (date) {
      setAddBody((prev) => ({ ...prev, warning_date: date }));
      setErrors((prev) => ({ ...prev, warningDate: "" }));
    } else {
      setAddBody((prev) => {
        const newState = { ...prev };
        delete newState.warning_date;
        return newState;
      });
    }
  };

  const handleCommentsChange = (e) => {
    setComments(e.target.value);
    setAddBody((prev) => ({ ...prev, comments: e.target.value }));
  };

  const handleAddWarning = () => {
    const newErrors = {};
    if (!collab) newErrors.collab = Localize("MANDATORY_FIELDS");
    if (!warningDate) newErrors.warningDate = Localize("MANDATORY_FIELDS");
    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      setShowError(true);
      return;
    }

    setShowError(false);

    if (addBody.id_collab && addBody.warning_date) {
      if (checkPermission(config.scopes.warnings, config.permissionTypes.edit)) {
        const createdBy = collabs.find((c) => c.id == editorId);
        const collabObj = collabs.find((c) => c.id == collab.value);
        const matricule = collabObj?.matricule ?? null;
        const collabNom = collabObj?.nom ?? null;
        const collabPrenom = collabObj?.prenom ?? null;
        const commentsVal = comments || "";
        const email = collabObj?.email ?? null;
        const poste = collabObj?.poste ?? null;
        const teamName = collabObj?.team ?? null;
        const warningDateTS = new Date(warningDate).getTime();
        const createdAt = new Date().getTime();
        const statusVal = status ? status.value : "sent";
        const categoryVal = category ? category.value : "warning";

        const addedWarning = {
          id_collab: collab.value,
          category: categoryVal,
          status: statusVal,
          comments: commentsVal,
          warning_date: warningDateTS,
          created_at: createdAt,
          updated_at: createdAt,
          created_by: createdBy,
          updated_by: {
            id: null,
            nom: null,
            prenom: null,
          },
          collab_nom: collabNom,
          collab_prenom: collabPrenom,
          collab_matricule: matricule,
          collab_email: email,
          poste: poste,
          team_name: teamName,
        };

        dispatch(setAddedWarningBody(addedWarning));
        dispatch(addWarning(addBody));
        handleClose();
      }
    }
  };

  const selectedCollab = collabOptions.find((o) => o.value === collab?.value) || null;

  return (
    <Dialog
      fullScreen
      open={!!visible}
      onClose={handleClose}
      TransitionComponent={Transition}
      sx={{
        zIndex: 1400,
        "& .MuiDialog-paper": {
          background: "var(--surface-ground, #f4f6f8)",
        },
      }}
    >
      <AppBar
        sx={{
          position: "sticky",
          top: 0,
          bgcolor: "rgba(255,255,255,0.82)",
          color: "text.primary",
          boxShadow: "0 1px 12px rgba(0,0,0,0.06)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          borderBottom: "1px solid rgba(0,0,0,0.06)",
        }}
      >
        <Toolbar sx={{ py: 0.5 }}>
          <IconButton
            edge="start"
            onClick={handleClose}
            aria-label="close"
            sx={{
              transition: TRANSITION_SMOOTH,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "rgba(0,0,0,0.06)",
                transform: "scale(1.08)",
              },
              "&:active": { transform: "scale(0.95)" },
            }}
          >
            <CloseIcon />
          </IconButton>
          <Typography
            sx={{
              ml: 1.5,
              flex: 1,
              fontWeight: 600,
              fontSize: "1.15rem",
              letterSpacing: "-0.02em",
            }}
            variant="h6"
          >
            <Langs str="ADD_WARNING" />
          </Typography>
        </Toolbar>
      </AppBar>

      <Box
        sx={{
          p: 2.5,
          pb: 14,
          overflowY: "auto",
          flex: 1,
          "@keyframes fadeSlideIn": {
            from: { opacity: 0, transform: "translateY(10px)" },
            to: { opacity: 1, transform: "translateY(0)" },
          },
          animation: "fadeSlideIn 0.35s cubic-bezier(0, 0, 0.2, 1) both",
        }}
      >
        {showError && (
          <Alert severity="error" sx={{ mb: 2.5, borderRadius: "12px" }}>
            <Langs str="MANDATORY_FIELDS" />
          </Alert>
        )}

        <Box
          sx={{
            background: "rgba(255,255,255,0.65)",
            backdropFilter: "blur(16px)",
            WebkitBackdropFilter: "blur(16px)",
            borderRadius: "16px",
            border: "1px solid rgba(255,255,255,0.45)",
            boxShadow: "0 2px 16px rgba(0,0,0,0.04), 0 0.5px 2px rgba(0,0,0,0.03)",
            p: 2.5,
            transition: TRANSITION_SMOOTH,
          }}
        >
          {/* Collab */}
          <Typography variant="body2" sx={fieldLabelSx}>
            Collaborateur *
          </Typography>
          <Autocomplete
            options={collabOptions}
            getOptionLabel={(option) => option.label || ""}
            value={selectedCollab}
            onChange={handleCollabChange}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Sélectionnez un collaborateur"
                error={!!errors.collab}
                helperText={errors.collab || ""}
                sx={glassmorphicTextFieldSx}
              />
            )}
            slotProps={{
              popper: { sx: { zIndex: 1500 } },
            }}
          />

          {/* Category */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="category" />
          </Typography>
          <Autocomplete
            options={categoryOptions}
            getOptionLabel={(option) => option.label || ""}
            value={category}
            onChange={handleCategoryChange}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={Localize("category")}
                sx={glassmorphicTextFieldSx}
              />
            )}
            slotProps={{
              popper: { sx: { zIndex: 1500 } },
            }}
          />

          {/* Status */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="status" />
          </Typography>
          <Autocomplete
            options={statusOptions}
            getOptionLabel={(option) => option.label || ""}
            value={status}
            onChange={handleStatusChange}
            isOptionEqualToValue={(option, value) => option.value === value?.value}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={Localize("status")}
                sx={glassmorphicTextFieldSx}
              />
            )}
            slotProps={{
              popper: { sx: { zIndex: 1500 } },
            }}
          />

          {/* Warning Date */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="warning_date" /> *
          </Typography>
          <TextField
            type="date"
            fullWidth
            value={warningDate}
            onChange={handleWarningDateChange}
            error={!!errors.warningDate}
            helperText={errors.warningDate || ""}
            sx={glassmorphicTextFieldSx}
          />

          {/* Description / Comments */}
          <Typography variant="body2" sx={fieldLabelSx}>
            <Langs str="DESCRIPTION" />
          </Typography>
          <TextField
            multiline
            rows={4}
            fullWidth
            value={comments}
            onChange={handleCommentsChange}
            inputProps={{ maxLength: 200 }}
            sx={glassmorphicTextFieldSx}
          />
        </Box>
      </Box>

      {/* Sticky bottom buttons */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          p: 2.5,
          bgcolor: "rgba(255,255,255,0.78)",
          backdropFilter: "blur(18px)",
          WebkitBackdropFilter: "blur(18px)",
          boxShadow: "0 -4px 24px rgba(0,0,0,0.06)",
          borderTop: "1px solid rgba(255,255,255,0.5)",
          zIndex: 1,
          display: "flex",
          gap: 1.5,
        }}
      >
        <Button
          variant="outlined"
          fullWidth
          onClick={clearInput}
          sx={{
            minHeight: 52,
            borderRadius: BORDER_RADIUS_BUTTON,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
            transition: TRANSITION_SMOOTH,
            "&:hover": { transform: "scale(1.02)" },
            "&:active": { transform: "scale(0.98)" },
          }}
        >
          Effacer
        </Button>
        <Button
          variant="contained"
          color="warning"
          fullWidth
          onClick={handleAddWarning}
          sx={{
            minHeight: 52,
            borderRadius: BORDER_RADIUS_BUTTON,
            textTransform: "none",
            fontWeight: 600,
            fontSize: "1rem",
            boxShadow: "0 4px 14px rgba(255,144,21,0.28)",
            transition: TRANSITION_SMOOTH,
            "&:hover": {
              transform: "scale(1.02)",
              boxShadow: "0 6px 20px rgba(255,144,21,0.35)",
            },
            "&:active": {
              transform: "scale(0.98)",
            },
          }}
        >
          Ajouter
        </Button>
      </Box>
    </Dialog>
  );
}
